import { merge } from 'lodash';
import { formatCurrency } from '@utils/utils';

// Shared ApexCharts look for statistics screens (matches the dashboard):
// rounded gradient columns, smooth orange line for meter readings, dashed grid,
// no toolbar. Colors are hex because Apex writes them into SVG attributes;
// dark mode text/grid colors are handled by CSS in assets/css/modern.css.
export const CHART_COLORS = {
  column: '#0284c7',
  columnTo: '#22d3ee',
  line: '#f97316',
  muted: '#64748b',
  grid: '#eef1f6'
};

const FONT = "'Inter Tight', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const shortNumber = (val) => {
  const n = Number(val) || 0;
  if (Math.abs(n) >= 1e6) return `${formatCurrency(n / 1e6, 1)}M`;
  if (Math.abs(n) >= 1e3) return `${formatCurrency(n / 1e3, 1)}k`;
  return formatCurrency(Math.round(n));
};

const base = {
  chart: {
    fontFamily: FONT,
    foreColor: CHART_COLORS.muted,
    toolbar: { show: false },
    zoom: { enabled: false },
    animations: { enabled: true, easing: 'easeinout', speed: 400 }
  },
  grid: {
    borderColor: CHART_COLORS.grid,
    strokeDashArray: 4,
    padding: { left: 8, right: 8, top: 0 }
  },
  xaxis: {
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { fontSize: '11.5px', fontWeight: 500 } }
  },
  dataLabels: { enabled: false },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    fontSize: '13px',
    fontWeight: 500,
    markers: { size: 5, shape: 'circle', width: 10, height: 10, radius: 10 },
    itemMargin: { horizontal: 10, vertical: 2 }
  },
  tooltip: { theme: 'light', shared: true, intersect: false },
  states: {
    hover: { filter: { type: 'darken', value: 0.92 } },
    active: { filter: { type: 'none' } }
  },
  plotOptions: {
    bar: {
      columnWidth: '55%',
      borderRadius: 5,
      borderRadiusApplication: 'end'
    }
  },
  fill: {
    type: ['gradient', 'solid'],
    gradient: {
      type: 'vertical',
      shadeIntensity: 0,
      gradientToColors: [CHART_COLORS.columnTo],
      opacityFrom: 1,
      opacityTo: 0.85,
      stops: [0, 100]
    }
  }
};

const yAxis = (title) => ({
  title: { text: title, style: { fontSize: '12px', fontWeight: 500, color: '#94a3b8' } },
  labels: { formatter: shortNumber, style: { fontSize: '11.5px' } },
  tooltip: { enabled: false }
});

/**
 * Consumption columns (one series).
 * @param {object} opts { categories | labels, unit, yTitle, onSelect(index), overrides }
 */
export const consumptionColumnOptions = ({
  categories,
  labels,
  unit = 'lít',
  yTitle = `Lượng nước (${unit})`,
  onSelect,
  overrides = {}
} = {}) =>
  merge(
    {},
    base,
    {
      colors: [CHART_COLORS.column],
      chart: {
        events: onSelect
          ? { dataPointSelection: (e, ctx, config) => onSelect(config.dataPointIndex) }
          : {}
      },
      ...(categories ? { xaxis: { categories } } : {}),
      ...(labels ? { labels } : {}),
      stroke: { show: true, width: 2, colors: ['transparent'] },
      yaxis: yAxis(yTitle),
      legend: { show: false },
      tooltip: { y: { formatter: (val) => `${formatCurrency(val)} ${unit}` } }
    },
    overrides
  );

/**
 * Hourly consumption columns + meter reading line on a second axis.
 * Series order must be [column, line].
 */
export const consumptionWithMeterOptions = ({
  labels,
  unit = 'lít',
  columnTitle = `Lượng nước (${unit})`,
  lineTitle = `Chỉ số đồng hồ (${unit})`,
  overrides = {}
} = {}) =>
  merge(
    {},
    base,
    {
      colors: [CHART_COLORS.column, CHART_COLORS.line],
      labels,
      stroke: { show: true, width: [0, 3], curve: 'smooth' },
      markers: {
        size: [0, 4],
        colors: ['#ffffff'],
        strokeColors: CHART_COLORS.line,
        strokeWidth: 2,
        hover: { size: 6 }
      },
      plotOptions: { bar: { columnWidth: '58%' } },
      yaxis: [yAxis(columnTitle), { ...yAxis(lineTitle), opposite: true }],
      tooltip: { y: { formatter: (val) => `${formatCurrency(val)} ${unit}` } }
    },
    overrides
  );
